/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import ButtonGroup from '../../../../client/js/components/form/ButtonGroup'
import FormSaveButton from '../../../../client/js/components/form/buttons/FormSaveButton'
import SmartForm from '../../../../client/js/components/form/SmartForm'
import RecordMetas from '../../../../client/js/components/RecordMetas'
import {
  createFieldInitializer,
  createFieldValidator,
  createFormValidator,
  removeUnknownFields
} from '../../../../client/js/lib/form'
import { AuthContext } from '../../../auth/components/AuthProvider'
import EmailField from '../../../user/components/fields/EmailField'
import MobileField from '../fields/MobileField'
import NameField from '../fields/NameField'
import PhoneField from '../fields/PhoneField'
import FormSection from '../FormSection'
import NoSensibleInfoHere from '../NoSensibleInfoHere'
import ContactSchema from '../../schemas/ContactSchema.mjs'
import { patchContact, postContact } from '../../lib/rest/contactAPIClient'
import DescriptionField from '../fields/DescriptionField'
import EnabledField from '../../../user/components/fields/EnabledField'
import rest from '../../../../client/js/lib/rest'
import { stringify } from 'qs'
import FileDropZone from '../file/FileDropZone'
import { useMutation } from '@tanstack/react-query'
import { parseNumber } from '@jalik/form-parser'
import { queryClient } from '../../../../client/js/lib/query'
import { getContactListLink } from '../../lib/links/contactLinks'
import FormResetButton from '../../../../client/js/components/form/buttons/FormResetButton'
import { SimpleGrid, Stack } from '@mantine/core'
import FormNumberField from '../../../../client/js/components/form/fields/FormNumberField'
import useCustomForm from '../../../../client/js/hooks/useCustomForm'
import { useSettingsContext } from '../SettingsProvider'

export type ContactFormProps = {
  initialValues: Partial<Contact>
}

function ContactForm (props: ContactFormProps) {
  const intl = useIntl()
  const { initialValues } = props
  const auth = useContext(AuthContext)
  const id = initialValues ? initialValues.id : null
  const editable = auth.hasPermission('write:contact')

  const { settings } = useSettingsContext()

  const form = useCustomForm({
    disabled: !editable,
    initialValues,
    initializeField: createFieldInitializer(ContactSchema),
    validate: createFormValidator(ContactSchema),
    validateField: createFieldValidator(ContactSchema),
    onSubmit: (submitValues) => {
      const finalValues = removeUnknownFields(submitValues, ContactSchema)
      return id > 0
        ? patchContact(id, finalValues)
        : postContact(finalValues)
    },
    onSuccess () {
      queryClient.invalidateQueries({ queryKey: ['contact', id] })
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
    }
  })

  const { values, setValue } = form

  const uploader = useMutation({
    mutationKey: ['uploadContactPhoto', id],
    mutationFn: async (file: File) => {
      const fd = new FormData()
      fd.append('photo', file)

      if (id != null) {
        fd.append('contactId', String(id))
      }
      const resp = await rest.post('/api/contacts/photos', { body: fd })
      const photo = await resp.json()
      setValue('photo', photo)
      return photo
    }
  })

  const handleDeletePhoto = useCallback(async () => {
    const params = { contactId: values.id }
    await rest.delete(`/api/contacts/photo?${stringify(params)}`)
    setValue('photo', undefined)
  }, [setValue, values.id])

  const { photo } = values

  return (
    <SmartForm
      context={form}
      redirectUrl={id == null ? getContactListLink() : undefined}
    >
      <Stack>
        {initialValues
          ? <RecordMetas metas={initialValues} />
          : null}

        <SimpleGrid cols={{ lg: 2 }}>
          <FormSection
            defaultOpened
            title={intl.formatMessage({
              id: 'general',
              defaultMessage: 'Général'
            })}
          >
            <Stack>
              <NameField />
              <DescriptionField description={<NoSensibleInfoHere />} />
              <MobileField />
              <PhoneField />
              <EmailField />
              <FormNumberField
                name="position"
                parser={parseNumber}
                step={1}
                label={intl.formatMessage({
                  id: 'position',
                  defaultMessage: 'Position'
                })}
              />
              <EnabledField />
            </Stack>
          </FormSection>

          <FormSection
            defaultOpened
            title={intl.formatMessage({
              id: 'photo',
              defaultMessage: 'Photo'
            })}
          >
            {id != null
              ? <FileDropZone
                accept={settings.CONTACT_PHOTO_FILE_TYPES}
                maxSize={settings.CONTACT_PHOTO_FILE_MAX_SIZE}
                contentLength={photo?.size}
                lastModified={photo?.date}
                loading={uploader.isPending}
                onChange={uploader.mutate}
                onDelete={handleDeletePhoto}
                url={photo?.url}
                value={photo?.url}
              >
                {photo != null
                  ? <img
                    alt={photo.alt || values.name}
                    src={photo.url}
                    style={{ width: 256, height: 256 }}
                  />
                  : <div>
                    Dimensions recommandées : <b>512 x 512 pixels</b>
                    <br />
                    La photo sera redimensionnée et coupée si nécessaire pour être au format carré.
                  </div>
                }
              </FileDropZone>
              : null}
          </FormSection>
        </SimpleGrid>

        {editable
          ? <ButtonGroup>
            <FormResetButton />
            <FormSaveButton />
          </ButtonGroup>
          : null}
      </Stack>
    </SmartForm>
  )
}

export default ContactForm
